import React from 'react';
import { CalendarDays, Heart, Users, Plane, UserRound, UserCheck } from 'lucide-react';
import { useCountUp } from '../../shared/Motion';

export const TeamCalendarHeader = () => (
  <div className="an-rise flex items-center justify-between gap-6" data-testid="tc-header">
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 min-w-0">
      <div className="h-16 w-16 shrink-0 rounded-2xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100/80 text-violet-600 flex items-center justify-center shadow-[0_10px_30px_-16px_rgba(109,78,224,.45)]">
        <CalendarDays className="h-7 w-7" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h1 className="text-[24px] sm:text-[28px] font-extrabold text-slate-900 tracking-tight leading-none" data-testid="tc-title">Team Leave Calendar</h1>
        <p className="text-[13.5px] text-slate-500 mt-1.5">View and manage team leaves across your organization.</p>
      </div>
    </div>
    <div className="hidden md:flex items-center gap-5 shrink-0 pr-1" aria-hidden="true">
      <div className="hidden xl:block text-right leading-tight -rotate-3">
        <span className="an-accent block text-[18px] leading-5">Stronger Teams</span>
        <span className="an-accent block text-[15px] leading-5">Build Brighter Tomorrows <Heart className="inline h-3.5 w-3.5 -mt-1 text-violet-500" /></span>
      </div>
      <img src="/leave-illustration.png" alt="" className="h-[88px] w-auto select-none drop-shadow-[0_18px_28px_rgba(79,70,229,.18)]" draggable={false} />
    </div>
  </div>
);

const Stat = ({ icon: Icon, label, value, tone, testId, delay }) => {
  const n = useCountUp(value ?? 0, 800);
  return (
    <div className={`lv-card lv-lift an-rise flex items-center gap-3.5 px-4 py-3 ${tone.card}`} style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
      <span className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${tone.icon}`}><Icon className="h-5 w-5" strokeWidth={2} /></span>
      <div className="min-w-0">
        <p className="text-[24px] font-extrabold text-slate-900 leading-none tabular-nums" data-testid={`${testId}-value`}>{value == null ? '—' : n}</p>
        <p className="text-[12.5px] text-slate-500 mt-1 truncate">{label}</p>
      </div>
    </div>
  );
};

// Values come from live data (team-availability members, calendar entries, pending-approvals total) — never hardcoded.
export const TeamCalendarSummary = ({ total, onLeave, pending }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tc-summary">
    <Stat icon={Users} label="Total Team Members" value={total} testId="tc-summary-total" delay={60} tone={{ card: 'bg-gradient-to-br from-white to-blue-50/70', icon: 'bg-blue-100 text-blue-600' }} />
    <Stat icon={Plane} label="On Leave" value={onLeave} testId="tc-summary-on-leave" delay={120} tone={{ card: 'bg-gradient-to-br from-white to-rose-50/70', icon: 'bg-rose-100 text-rose-600' }} />
    <Stat icon={UserRound} label="Pending Approval" value={pending} testId="tc-summary-pending" delay={180} tone={{ card: 'bg-gradient-to-br from-white to-amber-50/70', icon: 'bg-amber-100 text-amber-600' }} />
    <Stat icon={UserCheck} label="Available" value={total == null || onLeave == null ? null : Math.max(0, total - onLeave)} testId="tc-summary-available" delay={240} tone={{ card: 'bg-gradient-to-br from-white to-emerald-50/70', icon: 'bg-emerald-100 text-emerald-600' }} />
  </div>
);
